/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React, { useState, useRef, useEffect } from "react";

import styled from "styled-components";

import AudioPlayer from "./AudioPlayer"; // Adjust the path based on your directory structure
import { RecordingIcon } from "./RecordingIcon";

const TranscriptionDemo: React.FC = () => {
  const mediaRecorder = useRef<MediaRecorder | null>(null);

  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [transcription, setTranscription] = useState<string | null>(null);

  const startRecording = () => {
    navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
      mediaRecorder.current = new MediaRecorder(stream);

      // Only set the audio blob here. Don't call uploadAudio directly.
      mediaRecorder.current.ondataavailable = (event) => {
        setAudioBlob(event.data);
      };

      mediaRecorder.current.start();
      setRecording(true);
    });
  };

  const stopRecording = () => {
    if (mediaRecorder.current) {
      mediaRecorder.current.stop();
      setRecording(false);
    }
  };

  useEffect(() => {
    if (audioBlob) {
      uploadAudio();
    }
  }, [audioBlob]);

  const uploadAudio = async () => {
    if (!audioBlob) return;

    const formData = new FormData();
    formData.append("file", audioBlob);

    const response = await fetch(`${process.env.API_URL!}/demo/transcribe`, {
      method: "POST",
      body: formData,
    });

    const result = await response.json();
    if (result.text) {
      setTranscription(result.text);
    } else {
      console.error("No transcription found");
    }
  };

  return (
    <div>
      <CenterContainer>
        {recording ? (
          <RecordingButton onClick={stopRecording}>
            <BlackSquare /> {/* SVG for stop button or any other visual */}
          </RecordingButton>
        ) : (
          <RecordingButton onClick={startRecording}>
            <RecordingIcon />
          </RecordingButton>
        )}
      </CenterContainer>
      <CenterContainer>{audioBlob && <AudioPlayer src={URL.createObjectURL(audioBlob)} />}</CenterContainer>
      {audioBlob && <TextBox>{transcription && <p>{transcription}</p>}</TextBox>}
    </div>
  );
};

export default TranscriptionDemo;

const CenterContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
`;

const TextBox = styled.div`
  width: 200px;
  height: 150px;
  border: 1px solid black;
  background-color: white;
  padding: 10px;
  overflow-y: auto; // Make it scrollable vertically
  font-family: Syne;
`;

const RecordingButton = styled.button`
  width: 80px;
  height: 80px;
  background-color: #fff;
  border: 1px solid #000;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  outline: none;

  &:hover {
    background-color: #f7f7f7;
  }

  &:active {
    background-color: #e5e5e5;
  }

  svg {
    width: 40px;
    height: 40px;
  }
`;
const BlackSquare = styled.div`
  width: 20px;
  height: 20px;
  background-color: #eb4730;
`;
